import { EditorState, generateName } from '@common/Common';
import CorporateNavigation from '@common/Components/CorporateNavigation.vue';
import { MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog';
import MonthPickerDialog from '@common/Components/MonthPickerDialog.vue';
import { guid } from '@common/Guid';
import { MonthYear, MonthYearHelpers, ValidationError } from '@common/types';
import { ICommandStatus } from '@cqrs/cqrs';
import { ICommandBase } from '@Quest/cqrs';
import { CreateResourceMetricCommandV1, UpdateResourceMetricCommandV1 } from '@Quest/Metrics/Domain/commands-core';
import MetricsCommands from "@Quest/Metrics/Domain/commands-vue-component";
import MetricsQueries from "@Quest/Metrics/Domain/queries-vue-component";
import { ResourceMetricConfigurationReadModel } from '@Quest/Metrics/Domain/readmodels';
import { ValueBalanceType, ValueCommitmentType } from '@Quest/Metrics/types';
import { Component, mixins } from 'vue-facing-decorator';
import { Route } from 'vue-router';


@Component({ components: { CorporateNavigation, MonthPickerDialog } })
export default class ResourceMetricConfigurationPage extends mixins(MetricsQueries, MetricsCommands)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get strategyId(): string { return this.$route.params.strategyId; }
    get pageMode(): EditorState { return this.$route.meta.mode; }
    get isEditMode(): boolean { return this.pageMode === EditorState.Editor; }

    rm: ResourceMetricConfigurationReadModel | null = null;
    initialized: boolean = false;
    errors: ValidationError[] = [];
    isSaving: boolean = false;
    isScheduleDependent: boolean = false;

    name: string | null = null;
    description: string | null = null;
    resourceId: string | null = null;

    startDateBoundary: MonthYear | null = null;
    endDateBoundary: MonthYear | null = null;
    defaultProjectStartDate: MonthYear | null = null;

    valueBalanceType: ValueBalanceType = ValueBalanceType.Costs;
    applyBenefitsConfidenceFactor: boolean = false;
    valueCommitmentType: ValueCommitmentType = ValueCommitmentType.Total;

    startDateBoundaryMonthPicker: MonthPickerDialogViewModel = new MonthPickerDialogViewModel();
    endDateBoundaryMonthPicker: MonthPickerDialogViewModel = new MonthPickerDialogViewModel();
    defaultProjectStartDateMonthPicker: MonthPickerDialogViewModel = new MonthPickerDialogViewModel();

    get pageHeading(): string { return this.isEditMode ? "Edit the metric" : "New resource metric"; }
    get saveButtonText(): string { return this.isSaving ? "Saving..." : "Save changes"; }
    get nameError(): string | null { return this.errors.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null; }
    get hasChanges(): boolean
    {
        if (!this.isEditMode)
        {
            return true;
        }

        if (this.isScheduleDependent !== (this.rm.ScheduleDependentSettings != null))
        {
            return true;
        }

        if (this.isScheduleDependent)
        {
            return this.name !== this.rm.Name ||
                this.description !== this.rm.Description ||
                this.resourceId !== this.rm.ResourceId ||
                this.valueBalanceType !== this.rm.ValueBalanceType ||
                this.applyBenefitsConfidenceFactor !== this.rm.ScheduleDependentSettings?.ApplyBenefitsConfidenceFactor ||
                this.valueCommitmentType !== this.rm.ScheduleDependentSettings?.ValueCommitmentType ||
                !MonthYearHelpers.areEqual(this.defaultProjectStartDate, this.rm.ScheduleDependentSettings?.DefaultProjectStartDate) ||
                !MonthYearHelpers.areEqual(this.startDateBoundary, this.rm.ScheduleDependentSettings?.StartDateBoundary) ||
                !MonthYearHelpers.areEqual(this.endDateBoundary, this.rm.ScheduleDependentSettings?.EndDateBoundary);
        }
        else
        {
            return this.name !== this.rm.Name ||
                this.description !== this.rm.Description ||
                this.resourceId !== this.rm.ResourceId ||
                this.valueBalanceType !== this.rm.ValueBalanceType;
        }
    }

    get startDateBoundaryLabel(): string | null { return MonthYearHelpers.toMonthYearLongString(this.startDateBoundary); }
    get endDateBoundaryLabel(): string | null { return MonthYearHelpers.toMonthYearLongString(this.endDateBoundary); }
    get defaultProjectStartDateLabel(): string | null { return MonthYearHelpers.toMonthYearLongString(this.defaultProjectStartDate); }


    showStartDateBoundaryMonthPicker()
    {
        this.startDateBoundaryMonthPicker.show(this.startDateBoundary, "Metric Start Date Boundary", false);
    }

    showEndDateBoundaryMonthPicker()
    {
        this.endDateBoundaryMonthPicker.show(this.endDateBoundary, "Metric End Date Boundary", false);
    }

    showDefaultProjectStartDateMonthPicker()
    {
        this.defaultProjectStartDateMonthPicker.show(this.defaultProjectStartDate, "Default Project Start Date", false);
    }

    

    handleCancel()
    {
        if (this.isEditMode)
        {
            this.$router.go(0);
        }
        else
        {
            this.$router.go(-1);
        }
    }

    handleBack()
    {
        this.$router.go(-1);
    }

    handleSave()
    {
        this.isSaving = true;
        this.errors = [];

        if (this.isEditMode)
        {
            this.invokeUpdateResourceMetricCommandV1(this.workspaceId, this.strategyId, this.rm.Id, this.name, this.description, this.resourceId, this.valueBalanceType,
                this.isScheduleDependent, this.defaultProjectStartDate, this.startDateBoundary, this.endDateBoundary, this.valueCommitmentType, this.applyBenefitsConfidenceFactor);
        }
        else
        {
            this.invokeCreateResourceMetricCommandV1(this.workspaceId, this.strategyId, guid(), this.name, this.description, this.resourceId, this.valueBalanceType,
                this.isScheduleDependent, this.defaultProjectStartDate, this.startDateBoundary, this.endDateBoundary, this.valueCommitmentType, this.applyBenefitsConfidenceFactor);
        }
    }

    handleDeleteMetricCommand(id: string)
    {
        if (confirm("Are you sure?") === true)
        {
            this.invokeDeleteMetricCommandV1(this.workspaceId, this.strategyId, id);
        }
    }


    mounted()
    {
        document.title = "Resource Metric Configuration";
        this.runResourceMetricConfigurationQueryV1(this.workspaceId, this.strategyId, this.$route.params.metricId ?? null);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "ResourceMetricConfigurationReadModel")
        {
            this.rm = model as ResourceMetricConfigurationReadModel;

            this.name = this.isEditMode ? this.rm.Name : generateName(this.rm.Name);
            this.description = this.rm.Description;
            this.resourceId = this.rm.ResourceId ?? null;
            this.valueBalanceType = this.rm.ValueBalanceType;

            this.isScheduleDependent = this.rm.ScheduleDependentSettings != null;
            this.applyBenefitsConfidenceFactor = this.rm.ScheduleDependentSettings?.ApplyBenefitsConfidenceFactor ?? false;
            this.valueCommitmentType = this.rm.ScheduleDependentSettings?.ValueCommitmentType ?? ValueCommitmentType.Total;

            this.defaultProjectStartDate = MonthYearHelpers.copyNullable(this.rm.ScheduleDependentSettings?.DefaultProjectStartDate);
            this.startDateBoundary = MonthYearHelpers.copyNullable(this.rm.ScheduleDependentSettings?.StartDateBoundary);
            this.endDateBoundary = MonthYearHelpers.copyNullable(this.rm.ScheduleDependentSettings?.EndDateBoundary);

            this.initialized = true;
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string)
    {
        if (CreateResourceMetricCommandV1.matchType(cmd))
        {
            this.$router.go(-1);
        }
        else if (UpdateResourceMetricCommandV1.matchType(cmd))
        {
            this.$router.go(0);
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (CreateResourceMetricCommandV1.matchType(status.Command))
        {
            this.errors = status.ValidationErrors;
            this.isSaving = false;
        }
        else if (UpdateResourceMetricCommandV1.matchType(status.Command))
        {
            this.errors = status.ValidationErrors;
            this.isSaving = false;
        }
    }

    beforeRouteLeave(to: Route, from: Route, next: (arg?: any) => void) 
    {
        if (this.hasChanges === true && this.isSaving === false) 
        {
            if (confirm("You have unsaved changes. Do you want to save them before leaving?")) 
            {
                this.handleSave();
                next();
            }
            else 
            {
                next();
            }
        }
        else 
        {
            next();
        }
    }
}